import React, { useMemo, useState } from 'react';
import { Button } from '@airdao/ui-library';
import plus from '../../assets/icons/plus.svg';
import Table from '../../components/Table';
import {
  marketsTableHeading, presaleWhitelist,
  vestingOptions,
} from '../../utils/constants';
import useMarkets from '../../contexts/MarketsContext/useMarkets';
import { MarketContextType, MarketType } from '../../contexts/MarketsContext/provider.interface';
import { Link } from 'react-router-dom';
import { BigNumber, utils } from 'ethers';
import { timestampToDate } from '../../utils/timestampToDate';
import { getTokenByAddress } from '../../utils/getTokenByAddress';
import { displayAmount, formatDecimals } from '../../utils/formatDecimals';
import Select from '../../components/Select';
import noResults from '../../assets/icons/no-results.svg';
import PayoutTokenCell from '../../components/PayoutTokenCell';

const sortActive = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
];

function filterByTime(a: MarketType, b: MarketType) {
  // @ts-ignore
  return new Date(b.createdAt) - new Date(a.createdAt);
}

const Markets = () => {
  const { markets, closedMarkets, tokens, isLoading }: MarketContextType =
    useMarkets();

  const [sortType, setSortType] = useState(sortActive[1].value);
  const [tokenSortType, setTokenSortType] = useState('all');

  const currentMarkets = useMemo(() => {
    let arr = [...markets, ...closedMarkets];
    if (sortType === 'active') {
      arr = markets.sort(filterByTime);
    } else if (sortType === 'closed') {
      arr = closedMarkets;
    }
    return arr;
  }, [markets, closedMarkets, sortType]);

  const renderTable = (marketsList: MarketType[]) => {
    return marketsList
      .filter(el => {
        if (tokenSortType === 'all') {
          return true;
        } else {
          return el.quoteToken === tokenSortType;
        }
      })
      .map(el => {
        const payoutTokenData = getTokenByAddress(el.payoutToken, tokens);
        const quoteTokenData = getTokenByAddress(el.quoteToken, tokens);
        const bnAmount = BigNumber.from(el.capacity).add(el.sold);
        const amount = +utils.formatUnits(
          bnAmount,
          quoteTokenData?.decimals,
        );
        const vestingLabel = vestingOptions.find(
          item => item.value === +el.vesting,
        )?.label;
        const value = `$${(amount * (payoutTokenData?.price || 0)).toFixed(2)}`;
        const vesting =
          el.vestingType === 'Fixed-expiry'
            ? timestampToDate(+el.vesting)
            : vestingLabel;

        const soldPercent =
          BigNumber.from(el.sold)
            .mul(100)
            .div(bnAmount)
            .toNumber()

        return [
          // <span className="table__row-icon">
          //   <img src={payoutTokenData?.logoUrl} alt="" />
          //   {payoutTokenData?.symbol.toUpperCase()}
          // </span>,
          <PayoutTokenCell
            logo={payoutTokenData?.logoUrl}
            symbol={payoutTokenData?.symbol.toUpperCase()}
            percent={soldPercent}
          />,
          vesting,
          el.isLive ? formatDecimals(amount, el.payoutToken, tokens) : '-',
          el.isLive ? value : '-',
          <span style={{ color: el.discount > 0 ? 'green' : 'red' }}>
            {+el.discount.toFixed(2) ? el.discount.toFixed(2) + '%' : '-'}
          </span>,
          `$${displayAmount(el.askingPrice, 3)}`,
          <span className="table__row-icon">
            <img src={quoteTokenData?.logoUrl} alt="" />
            {quoteTokenData?.symbol.toUpperCase()}
          </span>,
          el.isLive ? (
            timestampToDate(el.start)
          ) : (
            <span className="exchange-info__status">Closed</span>
          ),
          `navigate:exchange/${el.id}`,
        ];
      });
  }

  const marketsTable = useMemo(() => {
    return renderTable(currentMarkets.filter((el) => !presaleWhitelist.includes(el.id)));
  }, [markets, closedMarkets, sortType, tokenSortType, tokens]);

  const presaleTable = useMemo(() => {
    console.log(currentMarkets.filter((el) => presaleWhitelist.includes(el.id)));
    return renderTable(currentMarkets.filter((el) => presaleWhitelist.includes(el.id)));
  }, [markets, closedMarkets, sortType, tokenSortType, tokens]);

  const tokenSortOptions = useMemo(() => {
    const activeTokens: any = {};

    currentMarkets.forEach(el => {
      activeTokens[el.quoteToken] = true;
    });

    return [
      { value: 'all', label: 'All' },
      ...Object.keys(activeTokens).map(el => {
        const currentToken = getTokenByAddress(el, tokens);
        return {
          value: currentToken?.contractAddress,
          label: (
            <span className="token-option">
              <img
                src={currentToken?.logoUrl}
                alt="logo"
                className="token-option__logo"
              />
              {currentToken?.symbol.toUpperCase()}
            </span>
          ),
        };
      }),
    ];
  }, [tokens, markets, closedMarkets, sortType]);

  const handleSort = (value: string) => {
    setSortType(value);
    setTokenSortType('all');
  };

  const marketsLoading = useMemo(() => {
    let loading;
    if (sortType === 'active') {
      loading = isLoading.markets;
    } else if (sortType === 'closed') {
      loading = isLoading.closedMarkets;
    } else {
      loading = isLoading.markets || isLoading.closedMarkets;
    }
    return loading || isLoading.tokens;
  }, [isLoading, sortType]);

  return (
    <div className="markets">
      <div className="markets__heading">
        <div className="markets__filters">
          <div className="markets__filters-item">
            <span className="markets__filters-label">Filtered by</span>
            <Select
              options={sortActive}
              value={sortType}
              onChange={handleSort}
            />
          </div>
          <div className="markets__filters-item">
            <span className="markets__filters-label">Payment token</span>
            <Select
              value={tokenSortType}
              onChange={setTokenSortType}
              options={tokenSortOptions}
            />
          </div>
        </div>
        <div className="markets__heading__buttons">
          <Link to="/create" className="markets__create">
            <Button
              type="primary"
              size="large"
              className="markets__create__button"
            >
              <img src={plus} alt="plus" />
              Create new bond
            </Button>
          </Link>
        </div>
      </div>
      <Table
        isLinks
        rowCells={presaleTable}
        gridTemplateClassName="markets__grid-template"
        isLoading={marketsLoading}
        label="Presale"
        headingCells={marketsTableHeading}
        additionalData={marketsTable}
        additionalLabel="Markets"
      />
      {!marketsLoading && !currentMarkets.length && (
        <div className="no-results">
          <img src={noResults} className="no-results__img" alt="no results" />
          <p className="no-results__text">
            Looks like there are no active bonds at the moment. <br />
            Check back later.
          </p>
        </div>
      )}
    </div>
  );
};

export default Markets;
